import { Backpack, Check, DoorOpen, Recycle, ShieldOff, Sparkles, Warehouse } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type RoomView, type RunState } from "../types";
import { EquipmentSlotIcon, SLOT_LABELS } from "../ui/EquipmentIcon";
import { Resource, ResourceIcon } from "../ui/Resources";
import { EMPTY_GAINS } from "../ui/defaults";
import { formatDisplayNumber, formatRunDuration } from "../ui/format";

export function RaidSettlement({
  view,
  busy,
  onContinue,
}: {
  view: RoomView;
  busy: boolean;
  onContinue: () => Promise<void>;
}) {
  const room = view.room;
  const me = room.participants.find((participant) => participant.userId === view.you);
  if (!me)
    return (
      <main className="settlement-screen">
        <h1>协作记录已失效</h1>
        <button className="primary-command" disabled={busy} onClick={() => void onContinue()}>
          <DoorOpen size={19} />
          返回大厅
        </button>
      </main>
    );
  const raid = room.mode === "raid";
  const reason =
    room.endReason === "victory"
      ? "Raid 胜利"
      : room.endReason === "timeout"
        ? "时限已到"
        : room.endReason === "retreat"
          ? "团队撤退"
          : raid
            ? "Raid 失败"
            : "矿道结束";
  const durationMs = Math.max(0, (room.endedAt ?? Date.now()) - (room.startedAt ?? room.createdAt));
  const contribution = room.boss ? (me.damage / Math.max(1, room.boss.maxHp)) * 100 : 0;
  return (
    <main className="settlement-screen raid-settlement">
      <div className="settlement-mark">
        {room.endReason === "victory" ? <Check size={34} /> : <ShieldOff size={34} />}
      </div>
      <p className="eyebrow">{raid ? "RAID SETTLED" : "CO-OP SETTLED"}</p>
      <h1>{reason}</h1>
      <div className="settlement-depth raid-settlement-boss">
        <span>{room.boss?.name ?? "协作矿道"}</span>
        <strong>{raid ? (room.boss?.hp ?? 0) : (room.duoDepth ?? 0)}</strong>
        <small>{raid ? `剩余生命 / ${room.boss?.maxHp ?? 0}` : "到达层数"}</small>
      </div>
      <div className="settlement-loot">
        <Resource kind="wood" value={me.wood} />
        <Resource kind="ore" value={me.ore} />
        <Resource kind="gold" value={me.gold} />
        <Resource kind="crystal" value={me.crystal} />
      </div>
      <div className="settlement-damage raid-settlement-stats">
        <strong>
          个人贡献 <span>{formatDisplayNumber(contribution)}%</span>
          <small>用时 {formatRunDuration(durationMs)}</small>
        </strong>
        <span>
          总伤害<b>{me.damage}</b>
        </span>
        <span>
          Boss 攻击<b>{room.boss?.attackCount ?? 0}</b>
        </span>
        <span>
          剩余生命<b>{me.hp}</b>
        </span>
        <span>
          团队人数<b>{room.participants.length}</b>
        </span>
      </div>
      {(me.equipmentDrops?.length ?? 0) > 0 && (
        <div className="settlement-equipment">
          <strong>
            <Backpack size={17} />
            新装备 {me.equipmentDrops?.length}
          </strong>
          {me.equipmentDrops?.map((item) => (
            <span key={item.id} className={`quality-${item.quality}`}>
              <EquipmentSlotIcon
                slot={item.slot}
                tier={item.tier}
                size={22}
                legendaryId={item.legendaryId}
                templateId={item.templateId}
              />
              {item.name} · {SLOT_LABELS[item.slot]}
            </span>
          ))}
        </div>
      )}
      {(me.autoSoldEquipment ?? 0) > 0 && (
        <div className="settlement-auto-sell">
          <Recycle size={17} />
          <span>按品质设置出售 {me.autoSoldEquipment} 件</span>
          <strong>+{me.autoSoldGold ?? 0} 金币</strong>
        </div>
      )}
      {(me.equipmentOverflow ?? 0) > 0 && (
        <div className="settlement-overflow">
          <Warehouse size={17} />
          <span>背包已满，自动出售 {me.equipmentOverflow} 件</span>
          <strong>+{me.overflowGold ?? 0} 金币</strong>
        </div>
      )}
      {raid && (
        <p className={`raid-attempt-result${me.attemptConsumed ? " is-consumed" : ""}`}>
          {me.attemptConsumed ? "本周 Raid 次数已消耗" : "本次失败未消耗周次数"}
        </p>
      )}
      <button className="primary-command" disabled={busy} onClick={() => void onContinue()}>
        <DoorOpen size={19} />
        返回狩猎大厅
      </button>
    </main>
  );
}

export function Settlement({ run, onContinue }: { run: RunState; onContinue: () => void }) {
  const reason =
    run.endReason === "retreat"
      ? "安全撤退"
      : run.endReason === "test_mine_cleared"
        ? "矿区已突破"
        : "生命耗尽";
  const gains = run.progressionGains ?? EMPTY_GAINS;
  const damage = run.damageStats ?? { direct: 0, activeSkill: 0, passive: 0 };
  const totalDamage = damage.direct + damage.activeSkill + damage.passive;
  const durationMs = Math.max(0, run.durationMs ?? 0);
  const dps = durationMs > 0 ? totalDamage / (durationMs / 1000) : 0;
  const hpLoss = run.hpLossStats ?? { monsterAttack: 0, burn: 0, obstacle: 0, reflect: 0 };
  const totalHPLoss = hpLoss.monsterAttack + hpLoss.burn + hpLoss.obstacle + hpLoss.reflect;
  return (
    <main className="settlement-screen">
      <div className="settlement-mark">
        <Check size={34} />
      </div>
      <p className="eyebrow">RUN SETTLED</p>
      <h1>{reason}</h1>
      <div className="settlement-depth">
        <span>抵达深度</span>
        <strong>{run.maxDepth}</strong>
        <small>{run.maxDepth * 10}m</small>
      </div>
      <div className="settlement-loot">
        <Resource kind="wood" value={run.wood} />
        <Resource kind="ore" value={run.ore} />
        <Resource kind="gold" value={run.gold} />
        <Resource kind="crystal" value={run.crystal} />
      </div>
      {(run.settlementOverflow?.wood ?? 0) +
        (run.settlementOverflow?.ore ?? 0) +
        (run.settlementOverflow?.gold ?? 0) >
        0 && (
        <div className="settlement-overflow">
          <Warehouse size={17} />
          <span>仓库已满</span>
          <strong>
            <span>
              <ResourceIcon kind="wood" size={11} />
              {run.settlementOverflow?.wood ?? 0}
            </span>
            <span>
              <ResourceIcon kind="ore" size={11} />
              {run.settlementOverflow?.ore ?? 0}
            </span>
            <span>
              <ResourceIcon kind="gold" size={11} />
              {run.settlementOverflow?.gold ?? 0}
            </span>
          </strong>
        </div>
      )}
      <div className="settlement-growth">
        <strong>人物经验 +{gains.experience}</strong>
        <SettlementExperience transition={run.settlementProgression} />
        <span>攻击 +{gains.attackExperience}</span>
        <span>防御 +{gains.defenseExperience}</span>
        <span>智力 +{gains.intelligenceExperience}</span>
        <span>体质 +{gains.constitutionExperience}</span>
      </div>
      <div className="settlement-damage">
        <strong>
          伤害统计 <span>{totalDamage}</span>
          <small>用时 {formatRunDuration(durationMs)}</small>
        </strong>
        <span>
          直接伤害<b>{damage.direct}</b>
        </span>
        <span>
          主动技能<b>{damage.activeSkill}</b>
        </span>
        <span>
          被动技能<b>{damage.passive}</b>
        </span>
        <span className="settlement-dps">
          平均 DPS<b>{formatDisplayNumber(dps)}</b>
        </span>
      </div>
      <div className="settlement-damage hp-loss-summary">
        <strong>
          生命损失 <span>{totalHPLoss}</span>
          <small>按来源拆分</small>
        </strong>
        <span>
          怪物攻击<b>{hpLoss.monsterAttack}</b>
        </span>
        <span>
          灼烧<b>{hpLoss.burn}</b>
        </span>
        <span>
          障碍<b>{hpLoss.obstacle}</b>
        </span>
        <span>
          反伤<b>{hpLoss.reflect}</b>
        </span>
      </div>
      {(run.equipmentDrops?.length ?? 0) > 0 && (
        <div className="settlement-equipment">
          <strong>
            <Backpack size={17} />
            新装备 {run.equipmentDrops?.length}
          </strong>
          {run.equipmentDrops?.map((item) => (
            <span key={item.id} className={`quality-${item.quality}`}>
              <EquipmentSlotIcon
                slot={item.slot}
                tier={item.tier}
                size={22}
                legendaryId={item.legendaryId}
                templateId={item.templateId}
              />
              {item.name} · {SLOT_LABELS[item.slot]}
            </span>
          ))}
        </div>
      )}
      {(run.settlementAutoSell?.equipment ?? 0) > 0 && (
        <div className="settlement-auto-sell">
          <Recycle size={17} />
          <span>按品质设置出售 {run.settlementAutoSell?.equipment} 件</span>
          <strong>+{run.settlementAutoSell?.gold ?? 0} 金币</strong>
        </div>
      )}
      {(run.settlementOverflow?.equipment ?? 0) > 0 && (
        <div className="settlement-overflow">
          <Warehouse size={17} />
          <span>背包已满，自动出售 {run.settlementOverflow?.equipment} 件</span>
          <strong>+{run.settlementOverflow?.equipmentGold ?? 0} 金币</strong>
        </div>
      )}
      <p>结算资源已写入仓库，剩余生命按比例保留。</p>
      <button className="primary-command" onClick={onContinue}>
        <Backpack size={19} />
        返回装备
      </button>
    </main>
  );
}

export function SettlementExperience({
  transition,
}: {
  transition?: RunState["settlementProgression"];
}) {
  const [experience, setExperience] = useState(() => transition?.before.experience ?? 0);
  const [levelNotice, setLevelNotice] = useState<{
    level: number;
    count: number;
    serial: number;
  } | null>(null);
  const noticeSerial = useRef(0);

  const transitionKey = transition
    ? `${transition.before.experience}:${transition.after.experience}:${transition.after.level}`
    : "";
  useEffect(() => {
    if (!transition || transition.bands.length === 0) return;
    const startExperience = transition.before.experience;
    const endExperience = Math.max(startExperience, transition.after.experience);
    const gain = endExperience - startExperience;
    setExperience(startExperience);
    setLevelNotice(null);
    if (gain === 0) {
      setExperience(endExperience);
      return;
    }
    let frame = 0;
    let startedAt = 0;
    let previousExperience = startExperience;
    const animate = (timestamp: number) => {
      if (startedAt === 0) startedAt = timestamp;
      const linearProgress = Math.min(1, (timestamp - startedAt) / 3000);
      const easedProgress = 1 - Math.pow(1 - linearProgress, 3);
      const nextExperience =
        linearProgress >= 1 ? endExperience : Math.round(startExperience + gain * easedProgress);
      const crossed = transition.bands.filter(
        (band) =>
          band.level > transition.before.level &&
          band.startExperience > previousExperience &&
          band.startExperience <= nextExperience,
      );
      if (crossed.length > 0) {
        noticeSerial.current += 1;
        setLevelNotice({
          level: crossed[crossed.length - 1].level,
          count: crossed.length,
          serial: noticeSerial.current,
        });
      }
      previousExperience = nextExperience;
      setExperience(nextExperience);
      if (linearProgress < 1) frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [transitionKey]);

  if (!transition || transition.bands.length === 0) return null;
  const currentBand = transition.bands.reduce(
    (current, band) => (band.startExperience <= experience ? band : current),
    transition.bands[0],
  );
  const currentExperience = Math.max(0, experience - currentBand.startExperience);
  const progress =
    currentBand.nextLevelExperience > 0
      ? Math.min(100, (currentExperience / currentBand.nextLevelExperience) * 100)
      : 100;
  return (
    <div
      className="settlement-experience"
      aria-label={`人物等级 ${currentBand.level}，本级经验 ${currentExperience} / ${currentBand.nextLevelExperience || "已满级"}`}
    >
      <div className="settlement-experience-readout">
        <span>Lv.{currentBand.level}</span>
        <strong>
          {currentExperience.toLocaleString("zh-CN")} /{" "}
          {currentBand.nextLevelExperience > 0
            ? currentBand.nextLevelExperience.toLocaleString("zh-CN")
            : "满级"}
        </strong>
      </div>
      <div
        key={levelNotice?.serial ?? "initial"}
        className={`settlement-experience-track${levelNotice ? " is-leveling" : ""}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={currentBand.nextLevelExperience || 1}
        aria-valuenow={currentBand.nextLevelExperience > 0 ? currentExperience : 1}
      >
        <i style={{ width: `${progress}%` }} />
      </div>
      <div className="settlement-level-event" aria-live="polite">
        {levelNotice && (
          <span key={levelNotice.serial} className="settlement-level-up">
            <Sparkles size={14} />
            {levelNotice.count > 1 ? `连续升级 ×${levelNotice.count}` : "升级"} · Lv.
            {levelNotice.level}
          </span>
        )}
      </div>
    </div>
  );
}
